import { useQuery } from "@tanstack/react-query";
import { MessageSquare } from "lucide-react";
import type { FC } from "react";
import React, { useState } from "react";

import { getUserPresentableMessage } from "@core/foundation/error";
import { hasValue } from "@core/foundation/types";

import {
  EmptyView,
  ErrorView,
  LoadingView,
} from "@react/foundation/placeholder";

import type { Project } from "@apis/memory/models";

import { PageView } from "../../../shared/components/PageView.tsx";
import { useQueries } from "../../../shared/hooks/useQueries.ts";

import { GithubRepoTabs } from "./GithubRepoTabs.tsx";
import { GithubCommentList } from "./github-comment/GithubCommentList.tsx";

interface ProjectCommentsViewProps {
  readonly projectId: Project["id"];
}

export const ProjectCommentsView: FC<ProjectCommentsViewProps> = ({
  projectId,
}) => {
  const [githubRepoId, setGithubRepoId] = useState<null | number>(null);

  const { getMemoryProjectQueryOptions } = useQueries();
  const project = useQuery(getMemoryProjectQueryOptions({ id: projectId }));

  if (project.status === "pending") {
    return <LoadingView />;
  }

  if (project.status === "error") {
    return (
      <ErrorView
        description={getUserPresentableMessage(
          project.error,
          "Failed to load the project.",
        )}
      />
    );
  }

  const githubRepo =
    project.data.githubRepos.find(({ id }) => id === githubRepoId) ??
    project.data.githubRepos[0];

  return (
    <PageView
      icon={<MessageSquare />}
      subtitle={project.data.project.name}
      title="Comments"
    >
      {hasValue(githubRepo) ? (
        <div className="flex flex-col gap-2">
          {project.data.githubRepos.length > 1 && (
            <GithubRepoTabs
              githubRepos={project.data.githubRepos}
              onSelect={setGithubRepoId}
              selectedGithubRepoId={githubRepo.id}
            />
          )}
          <GithubCommentList githubRepoId={githubRepo.id} />
        </div>
      ) : (
        <EmptyView
          description="Connect a repository to the project to pull its comments."
          title="No repositories connected"
        />
      )}
    </PageView>
  );
};
